import { ButtonStyles } from "atoms/constants/button-styles";
import { UserSettingsKeys } from "constants/user-settings-keys";
import { AcknowledgmentKey } from "models/enumerations/acknowledgment-key";
import AcknowledgmentRecord from "models/view-models/acknowledgment-record";
import UserSettingRecord from "models/view-models/user-setting-record";
import { ConfirmationModal } from "molecules/modals/confirmation-modal";
import React from "react";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import useUserSettings from "utilities/hooks/domain/user-settings/use-user-settings";
import { AcknowledgmentService } from "utilities/services/acknowledgments/acknowledgement-service";
import { ToastManager } from "utilities/toast/toast-manager";
import Image from "atoms/images/image";
import Paragraph from "atoms/typography/paragraph";
import CasiLogo from "assets/images/casi-logo.png";
import useBreakpointRanges from "utilities/hooks/use-breakpoint-ranges";
import { ModalTypes } from "molecules/constants/modal-types";
import { ModalCloseButtonStyle } from "molecules/constants/modal-close-button-style";
import { useLocalization } from "utilities/hooks/use-localization";
import { useChatModalActions } from "utilities/hooks/domain/chat/use-chat-modal";
import { CasiUserAgreementContentType } from "models/interfaces/casi-user-agreement";
import Anchor from "atoms/anchors/anchor";
import { AnchorTargetTypes } from "@rsm-hcd/javascript-core";

interface CasiUserAgreementModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const CasiUserAgreementModal = ({
    onClose,
    isOpen,
}: CasiUserAgreementModalProps) => {
    const cssBaseClass = "c-casi-user-agreement-modal";

    const { globalState, setGlobalState } = useGlobalState();
    const { createOrUpdate } = useUserSettings();
    const { inMobileView } = useBreakpointRanges();
    const { t } = useLocalization();

    const { openChatModal } = useChatModalActions();

    const casiUserAgreement =
        globalState.getSystemSettings().casiUserAgreementObject;

    const handleConfirm = async () => {
        const userId = globalState.currentIdentity?.userId() ?? 0;
        const enableAIChatSetting =
            globalState
                .getUserSetting(UserSettingsKeys.EnableAIChat)
                ?.with({ value: "true" }) ??
            new UserSettingRecord({
                key: UserSettingsKeys.EnableAIChat,
                value: "true",
                type: "boolean",
            });

        try {
            await AcknowledgmentService.create(
                new AcknowledgmentRecord({
                    key: AcknowledgmentKey.CasiBetaAccess,
                })
            );

            const updatedSetting = await createOrUpdate(
                enableAIChatSetting,
                userId
            );

            if (updatedSetting == null) {
                ToastManager.error(
                    t("errors-actionResource_possessive", {
                        action: t("saving"),
                        resource: t("acknowledgment"),
                    })
                );
                return;
            }

            setGlobalState(globalState.updateUserSetting(updatedSetting));
            ToastManager.success(t("casiAccess"));
            onClose();
            openChatModal();
        } catch (error) {
            console.error("Error creating acknowledgment:", error);
            ToastManager.error(
                t("errors-actionResource_possessive", {
                    action: t("saving"),
                    resource: t("acknowledgment"),
                })
            );
            return;
        }
    };

    return (
        <ConfirmationModal
            isVisible={isOpen}
            onCancel={onClose}
            label={t("casiAgreementTitle")}
            message="" // intentionally left blank to use custom content
            cancelButtonText={t("neverMind")}
            confirmButtonStyle={ButtonStyles.Primary}
            confirmButtonText={t("acknowledge")}
            onConfirm={handleConfirm}
            cssClassName={cssBaseClass}
            type={inMobileView ? ModalTypes.Fullscreen : ModalTypes.Base}
            closeButtonStyle={ModalCloseButtonStyle.Hidden}>
            <div className={`${cssBaseClass}__content`}>
                <Image altText={"CASI Logo"} src={CasiLogo} />
                <Paragraph>
                    {casiUserAgreement?.agreementText.map((part, i) =>
                        part.type === CasiUserAgreementContentType.Text ? (
                            <span key={i}>{part.content}</span>
                        ) : (
                            <Anchor
                                key={i}
                                ariaLabel="CASI User Agreement Link"
                                to={part.href}
                                target={AnchorTargetTypes.Blank}
                                external={true}>
                                {part.content}
                            </Anchor>
                        )
                    )}
                </Paragraph>
            </div>
        </ConfirmationModal>
    );
};

export default CasiUserAgreementModal;
